/**
 * The one way this layer writes a cell into a `.csv` file.
 *
 * Two modules export spreadsheets — `report.ts` for a resource scan and
 * `resmon.ts` for a pasted server `resmon` dump — and each arrived with its own
 * `csvCell`. They were not the same function. `report.ts` quoted *and* guarded
 * against formula injection; `resmon.ts` only quoted, and `resmon.ts` is the one
 * whose cells hold a resource name and a note copied verbatim out of somebody
 * else's server console. The stricter guard was protecting the more trusted
 * input, and because the correct implementation existed ten files away, the
 * codebase read as though the problem was solved everywhere.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A MODULE OF ITS OWN RATHER THAN ONE IMPORTING THE OTHER
 *
 * Either import would have been wrong in a way that outlives this fix.
 * `resmon.ts` is a 93 KB log parser that imports nothing from this directory but
 * `normalize.ts` and its own data tables; making `report.ts` import from it
 * drags the whole parser into every bundle that only wanted to export a folder
 * scan. The reverse — `resmon.ts` importing `report.ts` — pulls in `analyze.ts`,
 * `findings.ts` and the entire analyzer graph to borrow eight lines, and points
 * the dependency arrow from the standalone tool into the layer it is meant to
 * stand apart from. A leaf module that imports nothing can be depended on by
 * both without either learning about the other, and it cannot form a cycle
 * because it has no edges out.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IT DOES NOT COVER
 *
 * This is the cell, not the file. Line endings, column order and the header row
 * stay with each exporter, because they are decisions about a document rather
 * than about a value — both happen to write CRLF, and that is agreement, not
 * shared code.
 */

/**
 * The four characters a spreadsheet reads as "this cell is a formula", plus the
 * two whitespace characters that hide one.
 *
 * Excel, LibreOffice and Google Sheets all evaluate a cell beginning `=`, `+`,
 * `-` or `@`, and a leading tab or carriage return is stripped before that test
 * is applied — so `\t=cmd|'/c calc'!A0` is the same attack wearing a hat. The
 * payload arrives here the ordinary way: a resource name out of a stranger's
 * `fxmanifest.lua`, or a note pasted from a server console the user does not
 * own. Nothing this layer writes itself starts with one of these, which is
 * exactly why the guard has to be about the input rather than about the author.
 */
const FORMULA_LEAD = /^[=+\-@\t\r]/;

/**
 * A cell a spreadsheet cannot misread, and cannot be made to execute.
 *
 * The quoting half is RFC 4180: a cell holding a comma, a double quote or a
 * newline is wrapped in quotes and its own quotes are doubled. A finding's
 * `detail` is prose and a `title` can carry a file name a user chose, so both
 * happen constantly rather than theoretically.
 *
 * The guard half prefixes an apostrophe, which every spreadsheet reads as "the
 * rest of this is text" and no spreadsheet displays. A value that is simply a
 * negative number is left alone: turning `-2.5` into text would break the
 * arithmetic the CSV exists to support, and a number cannot be a formula.
 */
export function csvCell(value: string): string {
  const guarded = FORMULA_LEAD.test(value) && !isPlainNumber(value) ? `'${value}` : value;
  return /[",\r\n]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}

/**
 * Whether the whole cell is one finite number.
 *
 * `Number("")` is `0`, so the emptiness test is not redundant — without it every
 * blank cell would count as a number, which is harmless here and would be a
 * silent hole the moment this predicate is reused for anything else.
 */
function isPlainNumber(value: string): boolean {
  return value.trim() !== "" && Number.isFinite(Number(value));
}
